/**
 * Settings → Brands & categories (admin only; App.js wraps it in <AdminRoute>, every endpoint requires admin).
 *
 * One tab per kind (brand / category / subcategory): the registered spelling, how many SKUs use it, the other
 * spellings still stored on SKUs and the aliases that now resolve to it. Rename / merge sends
 * POST /api/settings/catalog-names/rename; a 409 means the merge would leave two targets (or GST keys / fee
 * rules) in one scope, and the colliding rows are shown so the admin can delete or re-enter one first.
 * "Run clean-up" shows the dry-run report (POST …/normalise?dry_run=true), then Apply runs it for real.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Pencil, RefreshCw, Sparkles, Tags } from 'lucide-react';
import { useCountry } from '../contexts/CountryContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LoadError, errorMessage } from '../components/LoadError';
import { number } from '../components/sales/format';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/settings/catalog-names`;
const KINDS = [['brand', 'Brands'], ['category', 'Categories'], ['subcategory', 'Subcategories']];
const KIND_WORD = { brand: 'brand', category: 'category', subcategory: 'subcategory' };

/** The rows a 409 carries: targets, GST keys and fee rules that would share one scope after the merge. */
const CollisionList = ({ rows, testId = 'catalog-collisions' }) => {
  if (!rows?.length) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid={testId}>
      <p className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" aria-hidden="true" />Fix these before merging</p>
      <p className="mt-1 text-xs text-amber-800">Two rows would end up in the same scope. Delete or re-enter one of each pair, then try again.</p>
      <ul className="mt-2 space-y-1 text-xs">
        {rows.map((row, index) => (
          <li key={index} className="tabular-nums">
            {row.kind === 'target' && <>Target {row.month} · {row.platform} · “{row.category}” → “{row.new_category}” ({number(row.target_revenue)})</>}
            {row.kind === 'category_gst' && <>GST rate for “{row.category}” → “{row.new_category}” ({row.gst_pct}%)</>}
            {row.kind === 'fee_rule' && <>Fee rule {row.platform} · “{row.category}” → “{row.new_category}”</>}
          </li>
        ))}
      </ul>
    </div>
  );
};

const RenameDialog = ({ open, onOpenChange, kind, row, country, onDone }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [collisions, setCollisions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setValue(row?.name || ''); setCollisions(null); setError(null); }
  }, [open, row]);

  const save = async () => {
    setBusy(true); setCollisions(null); setError(null);
    try {
      const { data } = await axios.post(`${API}/rename`, { kind, old: row.name, new: value.trim(), country });
      toast.success(data.message, { description: `${number(data.skus)} SKU${data.skus === 1 ? '' : 's'}${data.targets ? `, ${number(data.targets)} target${data.targets === 1 ? '' : 's'}` : ''} updated` });
      onOpenChange(false);
      onDone();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.collisions) setCollisions(detail.collisions);
      setError(errorMessage(err, 'Could not rename'));
    } finally {
      setBusy(false);
    }
  };

  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="catalog-rename-dialog">
        <DialogHeader>
          <DialogTitle>Rename {KIND_WORD[kind]} “{row.name}”</DialogTitle>
          <DialogDescription>
            Every SKU{kind === 'category' ? ', target, GST rate and fee rule' : ''} using this name (in any spelling) moves to the new
            name. Type an existing name to merge the two. Names are stored in Title Case.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="catalog-rename-input">New name</Label>
          <Input id="catalog-rename-input" value={value} onChange={e => setValue(e.target.value)} maxLength={120} autoFocus
            data-testid="catalog-rename-input" onKeyDown={e => { if (e.key === 'Enter' && value.trim()) save(); }} />
          <p className="text-xs text-gray-500">{number(row.sku_count)} SKU{row.sku_count === 1 ? '' : 's'} use this name.</p>
        </div>
        {error && !collisions && <p className="text-sm text-red-700" role="alert" data-testid="catalog-rename-error">{error}</p>}
        <CollisionList rows={collisions} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || !value.trim() || value.trim() === row.name} data-testid="catalog-rename-save">
            {busy ? 'Saving…' : 'Rename'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CleanupDialog = ({ open, onOpenChange, country, onDone }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setReport(null);
    try {
      const { data } = await axios.post(`${API}/normalise`, null, { params: { dry_run: true, country } });
      setReport(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [country]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/normalise`, null, { params: { dry_run: false, country } });
      const counts = Object.entries(data.kinds || {}).map(([kind, c]) => `${kind}: ${number(c.skus)} SKUs`).join(', ');
      toast.success('Clean-up applied', { description: counts });
      onOpenChange(false);
      onDone();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.collisions) setReport(prev => ({ ...(prev || {}), collisions: detail.collisions, blocked: true }));
      toast.error(errorMessage(err, 'Could not apply the clean-up'));
    } finally {
      setApplying(false);
    }
  };

  const merges = report ? KINDS.map(([kind, label]) => ({ kind, label, merges: report.kinds?.[kind]?.merges || [] })) : [];
  const total = report?.changes || 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="catalog-cleanup-dialog">
        <DialogHeader>
          <DialogTitle>Clean up brand and category names</DialogTitle>
          <DialogDescription>
            Preview first: every spelling below is merged into one Title Case name across SKUs, targets, GST rates and fee rules.
            Nothing changes until you press Apply.
          </DialogDescription>
        </DialogHeader>
        {loading && <p className="text-sm text-gray-500" role="status">Checking names…</p>}
        {error && <LoadError what="the clean-up preview" error={error} onRetry={load} retrying={loading} testId="catalog-cleanup-error" />}
        {report && (
          <div className="space-y-4 text-sm">
            {total === 0 && !report.collisions?.length && (
              <p className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-green-800" data-testid="catalog-cleanup-clean">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />All names are already clean. Nothing to do.
              </p>
            )}
            <CollisionList rows={report.collisions} testId="catalog-cleanup-collisions" />
            {merges.filter(m => m.merges.length).map(m => (
              <section key={m.kind} aria-labelledby={`cleanup-${m.kind}`}>
                <h3 id={`cleanup-${m.kind}`} className="font-semibold text-gray-900">{m.label}: {m.merges.reduce((n, item) => n + item.variants.length, 0)} spelling(s) to merge</h3>
                <ul className="mt-1 divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {m.merges.map(item => (
                    <li key={item.name} className="px-3 py-2">
                      <span className="font-medium">{item.name}</span>
                      <span className="ml-2 text-xs text-gray-500">{number(item.sku_count)} SKU{item.sku_count === 1 ? '' : 's'} after merge</span>
                      <ul className="mt-0.5 text-xs text-gray-600">
                        {item.variants.map(v => <li key={v.value}>“{v.value}” ({number(v.sku_count)})</li>)}
                      </ul>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            {report.targets_newly_matching?.length > 0 && (
              <section className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-blue-900" data-testid="catalog-cleanup-targets">
                <p className="font-medium">{report.targets_newly_matching.length} target(s) will start matching sales</p>
                <p className="text-xs text-blue-800">Their category never matched a SKU exactly before, so their achievement will move after the clean-up.</p>
                <ul className="mt-1 text-xs">
                  {report.targets_newly_matching.map((t, i) => <li key={i}>{t.month} · {t.platform} · “{t.category}” → “{t.new_category}”</li>)}
                </ul>
              </section>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>Close</Button>
          <Button onClick={apply} disabled={applying || loading || !report || report.blocked || total === 0} data-testid="catalog-cleanup-apply">
            {applying ? 'Applying…' : `Apply${total ? ` (${number(total)} change${total === 1 ? '' : 's'})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function CatalogNamesPage() {
  const { country } = useCountry();
  const [kind, setKind] = useState('brand');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [cleanupOpen, setCleanupOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(API, { params: { kind, country } });
      setRows(data.rows || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [kind, country]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows || [];
    return (rows || []).filter(r => r.name.toLowerCase().includes(needle) || r.aliases.some(a => a.includes(needle)) || r.variants.some(v => v.toLowerCase().includes(needle)));
  }, [rows, search]);
  const pending = useMemo(() => (rows || []).reduce((n, r) => n + r.variants.length, 0), [rows]);

  return (
    <div className="space-y-6" data-testid="catalog-names-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Brands &amp; categories</h1>
          <p className="mt-1 text-sm text-gray-500">One spelling per name across SKU Master, sales, targets and profitability. Rename or merge here; new uploads are cleaned automatically.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="catalog-refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />Refresh
          </Button>
          <Button size="sm" onClick={() => setCleanupOpen(true)} data-testid="catalog-run-cleanup">
            <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />Run clean-up
          </Button>
        </div>
      </header>

      {pending > 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="catalog-pending-note">
          {number(pending)} other spelling{pending === 1 ? '' : 's'} still stored on SKUs for this tab. Reports already merge them; run the clean-up to rewrite the stored values.
        </p>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg"><Tags className="h-5 w-5 text-blue-500" aria-hidden="true" />Names in use</CardTitle>
              <CardDescription>SKU counts merge every spelling. Aliases are old spellings that now resolve to the name.</CardDescription>
            </div>
            <Tabs value={kind} onValueChange={setKind}>
              <TabsList>
                {KINDS.map(([value, label]) => <TabsTrigger key={value} value={value} data-testid={`catalog-tab-${value}`}>{label}</TabsTrigger>)}
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search names, aliases or old spellings" className="max-w-sm" aria-label="Search names" data-testid="catalog-search" />
          {error && !rows && <LoadError what="the names" error={error} onRetry={load} retrying={loading} testId="catalog-error" />}
          {!rows && !error && <p className="py-4 text-sm text-gray-500" role="status">Loading…</p>}
          {rows && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm" data-testid="catalog-table">
                <caption className="sr-only">{KINDS.find(([v]) => v === kind)?.[1]} with SKU counts, aliases and other stored spellings.</caption>
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left font-medium">Name</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">SKUs</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium">Other spellings on SKUs</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium">Aliases</th>
                    <th scope="col" className="w-28 px-3 py-2"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className={loading ? 'opacity-60' : ''}>
                  {visible.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500" data-testid="catalog-empty">{rows.length ? 'No names match your search.' : 'No SKUs with this field yet.'}</td></tr>
                  )}
                  {visible.map(row => (
                    <tr key={row.name} className="border-t border-gray-100" data-testid={`catalog-row-${row.name}`}>
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {row.name}
                        {row.reserved && <span className="ml-2 rounded-full bg-gray-100 px-1.5 text-[11px] font-medium text-gray-600">no value</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{number(row.sku_count)}</td>
                      <td className="px-3 py-2 text-xs text-amber-800">{row.variants.length ? row.variants.map(v => `“${v}”`).join(', ') : <span className="text-gray-400">—</span>}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{row.aliases.filter(a => a !== row.name.toLowerCase()).join(', ') || <span className="text-gray-400">—</span>}</td>
                      <td className="px-3 py-2 text-right">
                        {!row.reserved && (
                          <Button variant="outline" size="sm" onClick={() => setRenaming(row)} data-testid={`catalog-rename-${row.name}`}>
                            <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />Rename
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <RenameDialog open={Boolean(renaming)} onOpenChange={open => { if (!open) setRenaming(null); }} kind={kind} row={renaming} country={country} onDone={load} />
      <CleanupDialog open={cleanupOpen} onOpenChange={setCleanupOpen} country={country} onDone={load} />
    </div>
  );
}
